import { useState, useEffect } from 'preact/hooks';
import { getIngredientImpact } from '../../api/ingredients';
import { Button } from '../../components/button';
import { Badge } from '../../components/badge';
import { Table, type Column } from '../../components/table';
import { Spinner } from '../../components/spinner';
import { Message } from '../../components/message';
import { EmptyState } from '../../components/empty-state';
import type { IngredientImpactResponse, IngredientImpactEntry } from '../../api/types';

interface IngredientImpactProps {
  id: number;
  onBack: () => void;
}

const STATUS_VARIANT: Record<string, string> = {
  halal: 'ok',
  haram: 'error',
  mushbooh: 'warn',
  compliant: 'ok',
  non_compliant: 'error',
  caution: 'warn',
  unknown: 'info',
};

function statusBadge(status: string) {
  return <Badge label={status} variant={STATUS_VARIANT[status] ?? 'info'} />;
}

const productColumns: Column<IngredientImpactEntry>[] = [
  { header: 'Product', key: 'name' },
  { header: 'Barcode', key: 'barcode' },
  { header: 'Status', key: 'status', render: (r) => statusBadge(r.status) },
  { header: 'Note', key: 'note', render: (r) => r.note || '—' },
];

export function IngredientImpact({ id, onBack }: IngredientImpactProps) {
  const [data, setData] = useState<IngredientImpactResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    getIngredientImpact(id)
      .then(setData)
      .catch((e) => setError(e instanceof Error ? e.message : 'Failed to load'))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) return <Spinner text="Analyzing impact" />;
  if (error) return <Message text={error} type="error" />;
  if (!data) return null;

  return (
    <div>
      <div class="toolbar">
        <Button variant="ghost" onClick={onBack}>Back to list</Button>
        <h2 style={{ margin: 0, flex: 1 }}>
          Impact Analysis: {data.ingredient.canonical_name}
        </h2>
      </div>

      {data.frameworks.length === 0 ? (
        <EmptyState message="No framework verdicts reference this ingredient" />
      ) : (
        data.frameworks.map((fw) => (
          <div key={fw.framework} style={{ marginBottom: '24px' }}>
            <h3>{fw.framework}</h3>
            <div style={{ display: 'flex', gap: '8px', marginBottom: '12px', flexWrap: 'wrap' }}>
              {Object.entries(fw.statuses).map(([status, count]) => (
                <Badge
                  key={status}
                  label={`${status}: ${count}`}
                  variant={STATUS_VARIANT[status] ?? 'info'}
                />
              ))}
            </div>
            {fw.products.length > 0 && (
              <Table
                columns={productColumns}
                rows={fw.products}
              />
            )}
          </div>
        ))
      )}
    </div>
  );
}
